"use client";
import { Fragment, useState } from "react";
import {
  Avatar,
  Box,
  Grid,
  CardHeader,
  Stack,
  Typography,
  IconButton
} from "@mui/material";
import {
  OpenInFullRounded as ExpandIcon,
  CloseRounded as CloseIcon,
  PlayArrowRounded as PlayArrowIcon
} from "@mui/icons-material";
import ReactPlayer from "react-player";

import { observer, useStore } from "../../../service/mobx";
import FilledCard from "../Filled";
import GameBadge from "../../GameBadge";

export const CardVideoSize1 = ({ video, tone }) => (
  <Card video={video} tone={tone} sx={{ p: 0 }}>
    <VideoThumbnail video={video} maxHeight={120} borderRadius="top" />
    <Box p={1} pt={0.5}>
      <Header size="small" video={video} pb={0} />
    </Box>
  </Card>
);
export const CardVideoSize2 = ({ video, tone }) => (
  <Card
    video={video}
    tone={tone}
    sx={{
      p: 2,
      display: "flex",
      width: "100%",
      justifyContent: "flex-start"
    }}
  >
    <Grid container columns={10} spacing={2}>
      <Grid size={{ compact: 10 }}>
        <VideoThumbnail video={video} width="100%" />
      </Grid>
      <Grid size={{ compact: 10 }}>
        <Header size="medium" video={video} />
        <Description text={video.description} />
      </Grid>
    </Grid>
  </Card>
);
export const CardVideoSize3 = ({ video, tone }) => (
  <Card
    video={video}
    tone={tone}
    sx={{
      display: "flex",
      justifyContent: "flex-start",
      maxHeight: 480,
      maxWidth: "100%",
      boxSizing: "border-box"
    }}
  >
    <Grid container columns={10} width="100%">
      <Grid size={{ compact: 10, medium: 6 }}>
        <VideoThumbnail video={video} />
      </Grid>
      <Grid
        size={{ compact: 10, expanded: "grow" }}
        px={{ compact: 0, expanded: 2 }}
        py={{ compact: 1, expanded: 2 }}
        pb={{ compact: 4, expanded: 0 }}
      >
        <Header video={video} />
        <Description text={video.description} />
      </Grid>
    </Grid>
  </Card>
);
const PlayIcon = () => (
  <IconButton
    size="large"
    sx={theme => ({
      bgcolor: "var(--primary-color)",
      opacity: 0.62,
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      transition: theme.transitions.create("all")
    })}
  >
    <PlayArrowIcon sx={{ color: "var(--primary-on-color)" }} />
  </IconButton>
);
function VideoThumbnail({
  video,
  width = "100%",
  maxHeight,
  borderRadius = "round"
}) {
  const [noImage, setNoImage] = useState(false);

  return (
    <Box
      maxWidth="100%"
      boxSizing="border-box"
      position="relative"
      sx={{
        "&:hover button": {
          opacity: 1
        }
      }}
    >
      {noImage ? null : (
        <Box
          component="img"
          alt={video.title}
          width={width}
          maxWidth="100%"
          maxHeight={maxHeight}
          onError={() => setNoImage(true)}
          src={video.img || video.thumbnails?.standard?.url || video.thumbnail}
          sx={theme => ({
            objectFit: "cover",
            borderRadius: theme.shape.md[borderRadius]
          })}
        />
      )}
      <PlayIcon />
    </Box>
  );
}
export function Subheader({ video }) {
  const { utilities } = useStore();
  const items = [
    video.viewCount || video.views
      ? `${utilities.formatNumber(video.viewCount || video.views)} views`
      : undefined,
    video.duration || video.durationSeconds
      ? `${Math.round((video.duration || video.durationSeconds) / 60)} mins`
      : undefined,
    video.published ? utilities.paperTime(video.published) : undefined,
    video.channel?.title
  ].filter(defined => defined);

  return items.length ? (
    <Stack direction="row" alignItems="center" flexWrap="wrap" width="100%">
      {items.map((content, index, array) => (
        <Fragment key={index}>
          {content}
          {index + 1 === array.length ? undefined : (
            <Box component="span" mx={0.5}>
              ·
            </Box>
          )}
        </Fragment>
      ))}
    </Stack>
  ) : null;
}
const Header = ({
  video,
  size = "large",
  color = "var(--surface-on-color)",
  pb = 3
}) => (
  <CardHeader
    avatar={
      video.scoreTrending ? (
        <GameBadge scoreTrending={video.scoreTrending} />
      ) : video.channel?.img ? (
        <Avatar src={video.channel.img} />
      ) : undefined
    }
    title={video.title}
    titleTypographyProps={{
      color,
      variant: `title${size === "large" ? "Lg" : "Md"}`,
      typography: {
        compact: "titleMd",
        expanded:
          size === "small"
            ? "labelLg"
            : `title${size === "large" ? "Lg" : "Md"}`
      },
      sx: theme => theme.typography.lineClamp(2)
    }}
    subheader={<Subheader video={video} />}
    subheaderTypographyProps={{
      color,
      display: "flex",
      alignItems: "center",
      typography: { compact: "labelSm", medium: "labelMd", expanded: "labelLg" }
    }}
    sx={{ p: 0, pb, alignItems: "flex-start", width: "100%" }}
  />
);
function Card({
  video,
  tone = "var(--surface-container-color)",
  children,
  sx = {}
}) {
  const { analytics } = useStore();
  const playVideo = useDialog(video);

  return (
    <FilledCard
      tone={tone}
      sx={{ p: 0, ...sx, cursor: "pointer" }}
      onClick={() => {
        analytics.track.event("Video Click", {
          publisher: video.publisher,
          videoID: video.videoID
        });
        playVideo();
      }}
    >
      {children}
    </FilledCard>
  );
}
const Description = ({ text }) =>
  text ? (
    <Typography
      maxWidth="75ch"
      variant="bodyMd"
      color="var(--surface-on-color)"
      sx={theme => theme.typography.lineClamp(4)}
    >
      {text}
    </Typography>
  ) : null;

const DialogPlayer = observer(function DialogPlayer({ video }) {
  const { analytics, device, dialog, videos } = useStore();
  const [ref, setRef] = useState(null);
  const [, setRemount] = useState();
  const { width = 0 } = ref?.getBoundingClientRect() ?? {};
  const height = device.isDesktop ? (width / 16) * 9 : "calc(100% - 64px)";
  const isFullScreen = document.fullscreenElement === null;

  return (
    <Box p={2} ref={setRef}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        spacing={2}
        pb={device.isDesktop || !isFullScreen ? 0 : 2}
      >
        {device.isDesktop ? null : (
          <IconButton
            aria-label="Play"
            size="small"
            onClick={() => dialog.set.open(false)}
          >
            <CloseIcon sx={{ color: "var(--dark-surface-on-color)" }} />
          </IconButton>
        )}
        {device.isDesktop || !isFullScreen ? null : (
          <IconButton
            aria-label="Play"
            size="small"
            onClick={() =>
              ref.requestFullscreen() && setTimeout(setRemount, 100, 1)
            }
          >
            <ExpandIcon sx={{ color: "var(--dark-surface-on-color)" }} />
          </IconButton>
        )}
        {!device.isDesktop && isFullScreen ? null : (
          <Header
            video={video}
            size="large"
            color="var(--dark-surface-on-color)"
          />
        )}
      </Stack>
      {!device.isDesktop && isFullScreen ? (
        <Header
          video={video}
          size="large"
          color="var(--dark-surface-on-color)"
        />
      ) : null}
      {video.url.includes("slideslive.com") ? (
        <Box
          src={video.clean.embed}
          component="iframe"
          width="100%"
          height="100vh"
          allow="autoplay; fullscreen"
          frameborder="0"
        />
      ) : (
        <ReactPlayer
          width="100%"
          height={height}
          src={video.url}
          playIcon={<PlayIcon />}
          playing={videos.playing === video.id}
          onEnded={() => {
            videos.set.playing();
            analytics.track.event("Video Finish", {
              publisher: video.publisher,
              videoID: video.videoID
            });
          }}
          onPlay={() => {
            videos.set.playing(video.id);
            analytics.track.event("Video Play", {
              publisher: video.publisher,
              videoID: video.videoID
            });
          }}
          wrapper={({ ...props }) => (
            <Box
              sx={{
                overflow: "hidden",
                borderRadius: "var(--shape-xl-round)"
              }}
              {...props}
            />
          )}
        />
      )}
    </Box>
  );
});

export function useDialog(video) {
  const { dialog, videos } = useStore();

  return () => {
    setTimeout(videos.set.playing, 2e3, video.id);

    dialog.configure({
      Content: () => <DialogPlayer video={video} />,
      sx: {
        paper: {
          overflow: "hidden",
          boxShadow: "unset",
          maxWidth: { compact: "100vw", large: "1200px" },
          maxHeight: { compact: "100vh", large: "800px" },
          height: { compact: "100vh" },
          width: { compact: "100vw" },
          bgcolor: "transparent"
        },
        scrim: {
          bgcolor: theme => theme.addAlpha("var(--surface-inverse-color)", 0.9),
          transition: theme =>
            `${theme.transitions.create("all", {
              duration: 2e3,
              easing: "var(--motion-easing-emphasized)"
            })} !important`
        }
      }
    });
  };
}
